
import {
  defineComponent, ref, computed, watch, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import { deleteKpi } from '@/composables/customStats/customKpi';
import type { CustomKpiDefinition } from '@/composables/customStats/customKpi.interfaces';

export default defineComponent({
  name: 'CustomKpiDeleteDialog',
  components: {
    Dialog,
    Button,
    InputText,
    Message,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    kpi: {
      type: Object as PropType<CustomKpiDefinition>,
      required: true,
    },
  },
  emits: ['update:visible', 'deleted'],
  setup(props, { emit }) {
    const { t, locale } = useI18n();

    const confirmation = ref('');
    const errorMessage = ref('');
    const isDeleting = ref(false);

    const displayedName = computed(() => {
      const n = props.kpi.name;
      return n?.[locale.value] ?? n?.en ?? Object.values(n ?? {})[0] ?? '';
    });

    // Le mot à taper est fixe selon la locale (FR='supprimer', EN='delete').
    // Le back accepte les deux quelque soit la langue (case-insensitive).
    const confirmWord = computed(() =>
      locale.value?.startsWith('fr') ? 'supprimer' : 'delete',
    );

    const isMatch = computed(
      () =>
        confirmation.value.trim().toLowerCase() ===
        confirmWord.value.toLowerCase(),
    );

    watch(
      () => props.visible,
      (v) => {
        if (v) {
          confirmation.value = '';
          errorMessage.value = '';
        }
      },
    );

    function onCancel() {
      emit('update:visible', false);
    }

    async function onConfirm() {
      if (!isMatch.value) return;
      isDeleting.value = true;
      errorMessage.value = '';
      try {
        await deleteKpi(props.kpi.id_custom_kpi, confirmation.value.trim());
        emit('deleted');
      } catch (err: any) {
        // err peut être un objet axios { message, status, ... } ou string.
        const msg = err?.message ?? err?.error ?? err;
        errorMessage.value =
          typeof msg === 'string' ? msg : JSON.stringify(msg);
      } finally {
        isDeleting.value = false;
      }
    }

    return {
      t,
      confirmation,
      errorMessage,
      isDeleting,
      displayedName,
      confirmWord,
      isMatch,
      onCancel,
      onConfirm,
    };
  },
});
